interface RecommendationQuery {
  text: string;
  rationale: string;
  category: string;
}

interface PatternRecommendationResponse {
  recommendations: RecommendationQuery[];
  pattern_summary: string;
  has_sufficient_data: boolean;
}

interface CachedRecommendations extends PatternRecommendationResponse {
  cached_at: number;
  user_id: string;
}

class PatternRecommendationService {
  private cache: Map<string, CachedRecommendations> = new Map();
  private readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
  private readonly API_BASE_URL = process.env.REACT_APP_FASTAPI_URL || 'http://localhost:8000';

  /**
   * Get personalized recommendations based on user's search patterns
   */
  async getPatternRecommendations(userId: string): Promise<PatternRecommendationResponse> {
    try {
      // Check cache first
      const cached = this.getCachedRecommendations(userId);
      if (cached) {
        console.log('Using cached pattern recommendations for user:', userId);
        return {
          recommendations: cached.recommendations,
          pattern_summary: cached.pattern_summary,
          has_sufficient_data: cached.has_sufficient_data
        };
      }

      console.log('Fetching new pattern recommendations for user:', userId);

      // Call backend API
      const response = await fetch(`${this.API_BASE_URL}/api/llm/generate-pattern-recommendations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error calling pattern recommendation API:', errorText);
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data: PatternRecommendationResponse = await response.json();
      
      // Cache the result
      this.cacheRecommendations(userId, data);

      console.log(`Generated ${data.recommendations.length} pattern recommendations for user ${userId}`);
      console.log(`Has sufficient data: ${data.has_sufficient_data}`);
      console.log(`Pattern summary: ${data.pattern_summary}`);
      console.log(`First recommendation: ${data.recommendations[0]?.text || 'none'}`);
      return data;

    } catch (error) {
      console.error('Error in getPatternRecommendations:', error);
      
      // Return fallback recommendations on error
      return this.getFallbackRecommendations();
    }
  }

  /**
   * Get cached recommendations if still valid
   */
  private getCachedRecommendations(userId: string): CachedRecommendations | null {
    const cached = this.cache.get(userId);
    if (!cached) return null;

    const now = Date.now();
    const isExpired = (now - cached.cached_at) > this.CACHE_DURATION;
    
    if (isExpired) {
      this.cache.delete(userId);
      return null;
    }

    return cached;
  }

  /**
   * Cache recommendations for a user
   */
  private cacheRecommendations(userId: string, data: PatternRecommendationResponse): void {
    const cached: CachedRecommendations = {
      ...data,
      cached_at: Date.now(),
      user_id: userId
    };

    this.cache.set(userId, cached);
  }

  /**
   * Invalidate cache for a user (call after new searches to refresh recommendations)
   */
  invalidateUserCache(userId: string): void {
    this.cache.delete(userId);
    console.log('Invalidated pattern recommendation cache for user:', userId);
  }

  /**
   * Get recommendations grouped by category
   */
  async getRecommendationsByCategory(userId: string): Promise<Record<string, RecommendationQuery[]>> {
    const recommendations = await this.getPatternRecommendations(userId);
    
    const grouped: Record<string, RecommendationQuery[]> = {};
    
    recommendations.recommendations.forEach(rec => {
      if (!grouped[rec.category]) {
        grouped[rec.category] = [];
      }
      grouped[rec.category].push(rec);
    });

    return grouped;
  }

  /**
   * Get a small set of top recommendations for compact display
   */
  async getTopRecommendations(userId: string, limit: number = 3): Promise<RecommendationQuery[]> {
    const recommendations = await this.getPatternRecommendations(userId);
    return recommendations.recommendations.slice(0, limit);
  }

  /**
   * Check if user has sufficient data for personalized recommendations
   */
  async hasPatternData(userId: string): Promise<boolean> {
    try {
      const recommendations = await this.getPatternRecommendations(userId);
      return recommendations.has_sufficient_data;
    } catch (error) {
      console.error('Error checking pattern data:', error);
      return false;
    }
  }

  /**
   * Fallback recommendations for errors or new users
   */
  private getFallbackRecommendations(): PatternRecommendationResponse {
    return {
      recommendations: [
        {
          text: "Why do fans think Jar Jar Binks was secretly a Sith Lord?",
          rationale: "A popular fan theory that explores hidden meanings in Star Wars",
          category: "Fan Theories"
        },
        {
          text: "How does Christopher Nolan create suspense without jumpscares?",
          rationale: "Explores cinematic techniques and filmmaking craft",
          category: "Techniques"
        },
        {
          text: "The hidden meaning behind the colors in The Matrix",
          rationale: "Visual symbolism and deeper themes in popular media",
          category: "Deep Dives"
        },
        {
          text: "Why Marvel movies all have similar third acts",
          rationale: "Pattern analysis in modern blockbuster storytelling",
          category: "Hidden Connections"
        },
        {
          text: "The real reason Tolkien invented so many languages",
          rationale: "Behind-the-scenes creative process and world-building",
          category: "Creative Process"
        }
      ],
      pattern_summary: "Showing trending explorations",
      has_sufficient_data: false
    };
  }

  /**
   * Refresh recommendations (force fetch new ones)
   */
  async refreshRecommendations(userId: string): Promise<PatternRecommendationResponse> {
    this.invalidateUserCache(userId);
    return this.getPatternRecommendations(userId);
  }

  /**
   * Get recommendation categories with display-friendly names
   */
  getCategoryDisplayNames(): Record<string, string> {
    return {
      "Deep Dives": "🔍 Deep Dives",
      "Creative Process": "🎬 Creative Process", 
      "Hidden Connections": "🔗 Hidden Connections",
      "Fan Theories": "💭 Fan Theories",
      "Techniques": "⚡ Techniques",
      "Influences": "💫 Influences"
    };
  }

  /**
   * Should we refresh recommendations based on user activity?
   */
  shouldRefreshForUser(userId: string, searchesSinceLastRefresh: number): boolean {
    // Refresh after every 3 new searches to keep recommendations current
    return searchesSinceLastRefresh >= 3;
  }
}

// Export singleton instance
export const patternRecommendationService = new PatternRecommendationService();
export type { RecommendationQuery, PatternRecommendationResponse };